import React, { useEffect, useState } from "react";
import { layoutStyle } from "../layout-ui/style";
import { Box, Grid, Typography, Paper, CircularProgress, Button } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BuildIcon from '@mui/icons-material/Build';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import StoreIcon from '@mui/icons-material/Store';
import { motion } from "framer-motion";
import { dashboardApi } from "../../common/api";
import { localStorageItemNames } from "../../common/constants/local-storage-item-names";
import { useCompany, useDealer } from "../../common/hooks";
import { getCompanyId } from "../../common/constants/commonFunction";

const modules = [
  { title: "Current Fleet Details", icon: <DirectionsCarIcon fontSize="large" />, path: "/current-fleet-details", desc: "Manage and track all vehicles" },
  { title: "Strategic Replacement Plan", icon: <CalendarMonthIcon fontSize="large" />, path: "/strategic-replacement-plan", desc: "Plan vehicle lifecycle" },
  { title: "Maintenance & Repair Cost Comparison", icon: <BuildIcon fontSize="large" />, path: "/maintenance-repair-cost-comparison", desc: "Track repair costs & health" },
  { title: "Fuel Economy Comparison", icon: <LocalGasStationIcon fontSize="large" />, path: "/fuel-economy-comparison", desc: "Monitor consumption & efficiency" },
  { title: "Recommended Acquisition Strategy", icon: <AttachMoneyIcon fontSize="large" />, path: "/recommended-acquisition-strategy", desc: "Financial planning & budget" },
  { title: "Suggested 5-Year Plan & Cost Comparison Analysis", icon: <AssessmentIcon fontSize="large" />, path: "/suggested-year-plan-cost-comparison-analysis", desc: "Comprehensive fleet reports" },
];

const KPI_CONFIG = [
  { key: "totalFleet", title: "Total Fleet", icon: <AttachMoneyIcon />, color: "#0475C2" },
  { key: "activeVendors", title: "Active Vendors", icon: <StoreIcon />, color: "#10B981" },
  { key: "pendingAnalysis", title: "Pending Analysis", icon: <ReportProblemIcon />, color: "#EF4444" },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 15 } },
};

const Dashboard = () => {
  const classes = layoutStyle();
  const userName = localStorage.getItem("name") || "User";
  const userType = localStorage.getItem("type") || "User";
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();

  const { companyNameHeader } = useCompany();
  const { companyDealerId, dealerNameHeader } = useDealer();

  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      const companyId = getCompanyId(userType, companyNameHeader) || localStorage.getItem(localStorageItemNames.companyId);
      if (!companyId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await dashboardApi.getSummary(companyId);
        // base-api wraps axios result in { data: response.data }
        // API response structure: { succeeded: true, data: { totalFleet, activeVendors, pendingAnalysis } }
        const resData = response?.data?.data || response?.data;
        if (resData) {
          setSummaryData(resData);
        }
      } catch (err) {
        console.error("Dashboard summary fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [companyNameHeader, dealerNameHeader, companyDealerId, userType]);

  return (
    <Box
      className={classes.dashboardMainContent}
      sx={(theme) => ({
        pt: 3,
        pb: 6,
        pr: 3,
        pl: 3,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '20px',
        boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(2, 6, 23, 0.06)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(160, 198, 232, 0.35)'}`,
        marginTop: '75px',
        marginRight: '20px',
        marginBottom: '20px',
        boxSizing: 'border-box',
      })}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.5px",
              fontFamily: "'Outfit', 'Inter', 'Roboto', 'Helvetica', sans-serif",
            }}
          >
            Welcome Back,{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #0860D3 0%, #78C2F8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {userName}
            </span>
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              mt: 0.5,
              fontWeight: 500,
              textTransform: "capitalize",
              fontFamily: "'Outfit', 'Inter', 'Roboto', 'Helvetica', sans-serif",
            }}
          >
            {userType} Portal • Overview Of Your Fleet Performance
          </Typography>
        </Box>
      </Box>

      {/* KPI Section */}
      <Grid
        container
        spacing={3}
        sx={{ mb: 4 }}
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {KPI_CONFIG.map((kpi, index) => {
          const value = summaryData ? summaryData[kpi.key] : null;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <Paper
                  sx={(theme) => ({
                    p: 3,
                    borderRadius: '16px',
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(160, 198, 232, 0.35)'}`,
                    boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(2, 6, 23, 0.06)',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: theme.palette.mode === 'dark' ? '0 10px 25px rgba(0,0,0,0.6)' : '0 10px 25px rgba(8, 96, 211, 0.12)',
                    },
                  })}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      p: 1,
                      borderRadius: 2,
                      background: muiTheme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.04)'
                        : `rgba(${parseInt(kpi.color.slice(1, 3), 16)},${parseInt(kpi.color.slice(3, 5), 16)},${parseInt(kpi.color.slice(5, 7), 16)},0.15)`,
                      color: kpi.color,
                    }}>
                      {kpi.icon}
                    </Box>
                  </Box>
                  <Typography sx={{ color: (theme) => theme.palette.text.secondary, fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                    {kpi.title}
                  </Typography>
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: kpi.color, mt: 0.5 }} />
                  ) : (
                    <Typography sx={{ color: (theme) => theme.palette.text.primary, fontSize: '1.75rem', fontWeight: 700 }}>
                      {value ?? "—"}
                    </Typography>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>

      {/* Modules Section */}
      <Typography variant="h6" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#94a3b8' : '#020617', fontWeight: 700, mb: 3 }}>
        Quick Access Modules
      </Typography>
      <Grid
        container
        spacing={3}
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {modules.map((mod, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <motion.div
              variants={itemVariants}
              whileHover="hover"
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Paper
                onClick={() => navigate(mod.path)}
                sx={(theme) => ({
                  p: 3,
                  height: '100%',
                  borderRadius: '16px',
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(160, 198, 232, 0.35)'}`,
                  boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(2, 6, 23, 0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.6)' : '0 12px 32px rgba(8, 96, 211, 0.12)',
                  }
                })}
              >
                <motion.div
                  className="icon-bg"
                  variants={{
                    initial: { scale: 1, opacity: 0.05 },
                    hover: { scale: 1.25, opacity: 0.12 }
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  style={{
                    position: 'absolute',
                    right: -20,
                    top: -20,
                    color: '#0860d3',
                    pointerEvents: "none",
                  }}
                >
                  <Box sx={{ "& > svg": { fontSize: 140 } }}>
                    {mod.icon}
                  </Box>
                </motion.div>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{
                    color: "#0860D3",
                    background: "rgba(8, 96, 211, 0.08)",
                    p: 1.5,
                    borderRadius: "12px",
                    display: 'inline-flex'
                  }}>
                    {mod.icon}
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ color: muiTheme.palette.mode === 'dark' ? '#94a3b8' : '#020617', fontWeight: 600, mb: 1, fontSize: "1.1rem" }}>
                  {mod.title}
                </Typography>
                <Typography sx={{ color: muiTheme.palette.mode === 'dark' ? '#9B9FA2' : '#6B7280', fontSize: "0.875rem", lineHeight: 1.5, mb: 2 }}>
                  {mod.desc}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Action
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(mod.path);
                    }}
                    sx={{
                      minWidth: 'auto',
                      px: 1.2,
                      py: 0.35,
                      borderRadius: '999px',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                    }}
                  >
                    Open
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;
